import { useSidebarSections } from "../navigation/navigationConfig";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { memo, useCallback, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import Collapse from "react-bootstrap/Collapse";
import { logoutThunk, selectUser, resolveRoleKey, ROLE } from "../../auth";
import { PATHS } from "../../config/routePaths";
import LogoutModal from "../siteHeader/LogoutModal";
import BrandLogo from "../siteHeader/BrandLogo";

function linkIsActive(location, href) {
  if (!href) return false;
  if (!String(href).includes("?")) return location.pathname === href;
  return `${location.pathname}${location.search}` === href;
}

function sectionHasActiveItem(items, location) {
  return items.some((item) => linkIsActive(location, item.href));
}

const Sidebar = memo(({ showIcons, onNavigate, collapsed = false, onToggleCollapse, showCollapseToggle = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sections = useSidebarSections();
  const user = useSelector(selectUser);
  const roleKey = resolveRoleKey(user);
  const isReader = roleKey === ROLE.READER;
  const sidebarLogoUrl = String(user?.sidebarLogoUrl || "").trim();
  const [loggingOut, setLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [openSectionIds, setOpenSectionIds] = useState(() => new Set());

  useLayoutEffect(() => {
    setOpenSectionIds(() => {
      const next = new Set();
      for (const sec of sections) {
        if (sectionHasActiveItem(sec.items, location)) {
          next.add(sec.id);
          break;
        }
      }
      if (next.size === 0 && sections.length > 0) {
        next.add(sections[0].id);
      }
      return next;
    });
  }, [location, sections]);

  /** Exclusive accordion: one section open at a time (saves vertical space). */
  const toggleSection = useCallback((id) => {
    setOpenSectionIds((prev) => {
      if (prev.has(id)) {
        return new Set();
      }
      return new Set([id]);
    });
  }, []);

  const handleRouteClick = () => {
    if (typeof onNavigate === "function") onNavigate();
  };

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await dispatch(logoutThunk()).unwrap();
      setShowLogoutModal(false);
      handleRouteClick();
      navigate(PATHS.HOME);
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLoggingOut(false);
    }
  };

  const renderLink = (item, linkKey) => (
    <li className="nav-item w-100" key={linkKey}>
      <Link
        to={item.href}
        className={`sidebar-link d-flex align-items-center px-3 py-2 ${
          linkIsActive(location, item.href) ? "active" : ""
        } ${item.danger ? "text-danger" : ""}`}
        onClick={handleRouteClick}
        title={collapsed ? item.label : undefined}
      >
        {showIcons && item.icon && (
          <i className={`bi ${item.icon} ${collapsed ? "" : "me-2"} sidebar-link__icon`}></i>
        )}
        {!collapsed ? item.label : null}
      </Link>
    </li>
  );

  return (
    <nav id="sidebarMenu" className={`sidebar-nav h-100 d-flex flex-column ${collapsed ? "sidebar-nav--collapsed" : ""}`}>
      <div className="sidebar-nav__inner position-sticky top-0 pt-2 pt-md-3 d-flex flex-column h-100 min-h-0">
        <div className="sidebar-nav__header mb-2 px-2 flex-shrink-0">
          {isReader ? (
            <div
              className={`sidebar-nav__logo sidebar-nav__brand ${collapsed ? "sidebar-nav__logo--collapsed" : ""}`}
            >
              <BrandLogo />
            </div>
          ) : (
            <Link to={PATHS.APP_DASHBOARD} className={`sidebar-nav__logo ${collapsed ? "sidebar-nav__logo--collapsed" : ""}`}>
              {sidebarLogoUrl ? (
                <img
                  src={sidebarLogoUrl}
                  alt="Sidebar logo"
                  className="sidebar-nav__logo-img"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="sidebar-nav__logo-fallback">
                  <i className="bi bi-image" aria-hidden />
                </span>
              )}
            </Link>
          )}
          {showCollapseToggle ? (
            <button
              type="button"
              style={{ minHeight: "22px", minWidth: "22px", width: "22px", height: "22px" }}
              className="btn btn-sm  btn-outline-secondary px-0 mx-0 sidebar-nav__toggle-btn d-inline-flex align-items-center justify-content-center"
              onClick={onToggleCollapse}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <i className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-left"}`} />
            </button>
          ) : null}
        </div>

        <div className="sidebar-nav__scroll flex-grow-1 min-h-0">
          {collapsed ? (
            <ul className="sidebar-nav__links nav flex-column gap-1 flex-nowrap align-items-stretch w-100 m-0 p-0">
              {sections.flatMap((sec, sIdx) => {
                const rows = [];
                if (sIdx > 0) {
                  rows.push(
                    <li key={`sep-${sec.id}`} className="nav-item p-0 w-100" aria-hidden>
                      <div className="sidebar-nav__section-divider" />
                    </li>
                  );
                }
                sec.items.forEach((item, idx) => {
                  rows.push(renderLink(item, `${sec.id}-${item.href}-${idx}`));
                });
                return rows;
              })}
            </ul>
          ) : (
            <div className="sidebar-nav__sections d-flex flex-column gap-1">
              {sections.map((sec) => {
                const isOpen = openSectionIds.has(sec.id);
                const panelId = `sidebar-section-${sec.id}`;
                return (
                  <div key={sec.id} className="sidebar-nav__section">
                    <button
                      type="button"
                      className="sidebar-nav__section-toggle w-100 d-flex align-items-center justify-content-between gap-1 px-2 py-1"
                      onClick={() => toggleSection(sec.id)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      id={`${panelId}-btn`}
                    >
                      <span className="sidebar-nav__section-title text-start">{sec.label}</span>
                      <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"} sidebar-nav__section-chevron flex-shrink-0`} aria-hidden />
                    </button>
                    <Collapse in={isOpen}>
                      <div id={panelId} role="region" aria-labelledby={`${panelId}-btn`}>
                        <ul className="sidebar-nav__section-list nav flex-column gap-1 flex-nowrap align-items-stretch pb-1 m-0 p-0 w-100">
                          {sec.items.map((item, idx) => renderLink(item, `${sec.id}-${item.href}-${idx}`))}
                        </ul>
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {user?.uid ? (
          <div className="sidebar-bottom mt-auto pt-2 flex-shrink-0">
            <button
              type="button"
              className="sidebar-link sidebar-link--danger d-flex align-items-center px-3 py-2 text-danger border-0 w-100 bg-transparent"
              onClick={() => setShowLogoutModal(true)}
              disabled={loggingOut}
              title={collapsed ? "Logout" : undefined}
            >
              {showIcons && <i className={`bi bi-box-arrow-right ${collapsed ? "" : "me-2"} sidebar-link__icon`}></i>}
              {!collapsed ? (loggingOut ? "Logging out..." : "Logout") : null}
            </button>
          </div>
        ) : null}
      </div>
      {showLogoutModal && createPortal(
        <LogoutModal
          show={showLogoutModal}
          onConfirm={handleLogout}
          onCancel={() => {
            if (!loggingOut) setShowLogoutModal(false);
          }}
          loading={loggingOut}
        />,
        document.body
      )}
    </nav>
  );
});

Sidebar.displayName = "Sidebar";

export default Sidebar;
